// C4 재련 굴림(순수·결정적). 장부 연산(gearLedger reroll)과 확률표(odds.ts)·시험이 같은 함수를 쓴다. three·DOM import 금지.
// 씨앗 = hashSeed('rr|' + uid + '|' + n) — n은 장비의 재련 카운터라 같은 (uid, n)이면 언제나 같은 결과(재전송·두 탭 합치기에서 결과가 갈리지 않는다).
// 난수 소비 순서: 안 잠근 줄을 앞에서부터, 줄마다 [옵션 종류 1회(확정형의 첫 줄은 건너뜀)] → [값 q 1회]. 바꾸면 저장된 n의 결과가 달라진다.
import { AFFIX_FLOOR, affixDef, affixValueAt, hashSeed, nextRand, seedRng, type GearAffix, type GearItem } from '../gear';
import type { AffixId, EquipDef } from '../content/types';

/** 값 분위 경계(q 기준): 하 < 0.40 · 중 0.40~0.75 · 상 0.75~0.95 · 최상 ≥ 0.95 (PRD 4.3) */
export const TIER_BOUNDS: readonly number[] = [0.4, 0.75, 0.95];
export const TIER_NAMES: readonly string[] = ['하', '중', '상', '최상'];

/** 마스크로 잠긴 줄의 옵션 id 목록 */
function lockedIds(item: GearItem, mask: number): AffixId[] {
  const out: AffixId[] = [];
  for (let i = 0; i < item.affixes.length; i++) if (mask & (1 << i)) out.push(item.affixes[i].id);
  return out;
}

/**
 * 재련 결과 줄들: 잠근 줄(mask 비트)은 위치·값 그대로, 안 잠근 줄만 다시 뽑는다(줄 수 그대로).
 * 후보 = 정의 affixPool − 잠근 줄 id − 이미 뽑은 id(겹치지 않음). sure(확정형)가 있으면 첫 번째 안 잠근 줄이 그 옵션(값은 굴림).
 * 값 q = AFFIX_FLOOR[등급] + (1 − floor) × 난수. 호출자가 sure가 풀에 있고 잠근 줄 id가 아님을 보장한다
 */
export function rerollLines(item: GearItem, def: EquipDef, n: number, mask: number, sure?: AffixId): GearAffix[] {
  const rand = seedRng(hashSeed('rr|' + item.uid + '|' + n));
  const out = item.affixes.slice();
  const taken = lockedIds(item, mask);
  const floor = AFFIX_FLOOR[item.grade];
  let first = true;
  for (let i = 0; i < out.length; i++) {
    if (mask & (1 << i)) continue;
    let id: AffixId | undefined;
    if (first && sure !== undefined) id = sure;
    else {
      const cand = def.affixPool.filter((a) => taken.indexOf(a) < 0);
      if (cand.length > 0) id = cand[Math.min(cand.length - 1, Math.floor(nextRand(rand) * cand.length))];
    }
    first = false;
    const a = id === undefined ? undefined : affixDef(id);
    if (!a) continue; // 데이터 공백(후보 없음): 그 줄은 그대로 둔다
    taken.push(a.id);
    const q = floor + (1 - floor) * nextRand(rand);
    out[i] = { id: a.id, v: affixValueAt(a, q) };
  }
  return out;
}

/** 값 q(0..1) → 분위 번호 0(하)..3(최상) */
export function tierOfQ(q: number): number {
  return q >= TIER_BOUNDS[2] ? 3 : q >= TIER_BOUNDS[1] ? 2 : q >= TIER_BOUNDS[0] ? 1 : 0;
}
/** 개체 옵션 한 줄의 분위(범위 위 위치 q 기준). 모르는 옵션은 −1 */
export function lineTier(line: GearAffix): number {
  const a = affixDef(line.id);
  if (!a) return -1;
  const span = a.range[1] - a.range[0];
  if (!(span > 0)) return -1;
  // 값은 소수 4자리로 반올림돼 있어 경계에서 q가 미세하게 어긋날 수 있다 — 반올림 오차(반 단위)만큼 위로 본다
  return tierOfQ((line.v - a.range[0]) / span + 0.5e-4 / span);
}
